import { Badge, BlockStack, Button, Card, InlineStack, ProgressBar, Text } from '@shopify/polaris';
import React from 'react';
import { FitmentImportHistoryItem } from '../../app/api/agent';

export const formatLabelMap: Record<string, string> = {
    csv_xlsx: 'CSV/XLSX',
    aces_xml: 'ACES XML',
    atd_price_file: 'ATD Price File',
    product_tags: 'Product Tags',
    fitment_export: 'Fitment Export',
    unknown: 'Unknown',
};

const formatStageLabel = (stage?: string) => {
    if (!stage) {
        return null;
    }

    switch (stage.trim().toLowerCase()) {
        case 'processing':
            return 'Processing';
        case 'resolving_skus':
            return 'Resolving SKUs';
        case 'waiting_shopify_sync':
            return 'Waiting for Shopify sync';
        case 'processing_recovered_skus':
            return 'Processing recovered SKUs';
        case 'parsing':
            return 'Parsing';
        case 'applying':
            return 'Applying';
        case 'pricing':
            return 'Updating prices';
        case 'reading':
            return 'Reading tags';
        case 'exporting':
            return 'Exporting';
        case 'tagging':
            return 'Tagging';
        case 'completed':
            return 'Completed';
        case 'completed_with_skipped_skus':
            return 'Completed with skipped SKUs';
        case 'failed':
            return 'Failed';
        default:
            return stage;
    }
};

export const getStatusBadge = (state: string) => {
    const normalizedState = state.trim().toLowerCase();

    if (normalizedState === 'completed' || normalizedState === 'complete') {
        return <Badge tone="success">Complete</Badge>;
    }

    if (normalizedState === 'completed with skipped skus') {
        return <Badge tone="warning">Completed with skipped SKUs</Badge>;
    }

    if (normalizedState === 'pending review') {
        return <Badge tone="warning">Pending review</Badge>;
    }

    if (normalizedState === 'applied') {
        return <Badge tone="success">Applied</Badge>;
    }

    if (normalizedState === 'undone') {
        return <Badge>Undone</Badge>;
    }

    if (normalizedState === 'failed' || normalizedState === 'error') {
        return <Badge tone="critical">Error</Badge>;
    }

    if (normalizedState === 'queued' || normalizedState === 'pending') {
        return <Badge tone="info">Queued</Badge>;
    }

    if (normalizedState === 'waiting for shopify sync') {
        return <Badge progress="partiallyComplete" tone="info">Waiting for Shopify sync</Badge>;
    }

    if (normalizedState === 'resolving skus') {
        return <Badge progress="partiallyComplete" tone="info">Resolving SKUs</Badge>;
    }

    if (normalizedState === 'processing recovered skus') {
        return <Badge progress="partiallyComplete" tone="info">Processing recovered SKUs</Badge>;
    }

    if (normalizedState === 'processing' || normalizedState === 'in_progress' || normalizedState === 'in progress') {
        return <Badge progress="partiallyComplete" tone="info">In progress</Badge>;
    }

    if (normalizedState === 'exporting') {
        return <Badge progress="partiallyComplete" tone="info">Exporting</Badge>;
    }

    if (normalizedState === 'tagging in progress') {
        return <Badge progress="partiallyComplete" tone="info">Tagging In Progress</Badge>;
    }

    if (normalizedState === 'tagging complete') {
        return <Badge tone="success">Tagging Complete</Badge>;
    }

    if (normalizedState === 'tagging failed') {
        return <Badge tone="critical">Tagging Failed</Badge>;
    }

    if (normalizedState === 'cancelled' || normalizedState === 'canceled') {
        return <Badge tone="warning">Cancelled</Badge>;
    }

    return <Badge>{state}</Badge>;
};

export const getMappingRulesSummary = (item: FitmentImportHistoryItem) => {
    if (item.importFormat === 'atd_price_file') {
        return 'ATD price import: MAP updates variant price and cost updates inventory item cost.';
    }

    if (item.importFormat === 'product_tags') {
        return 'Product tag import: Shopify product tags are staged for review before app fitment mappings are changed.';
    }

    if (item.importFormat === 'fitment_export') {
        return 'Fitment export: only active product or variant assignments are exported; source vehicle catalogs are not dumped.';
    }

    if (item.importFormat === 'csv_xlsx') {
        return item.csvXlsxImportMode === 'replace_matched_skus'
            ? 'Mode: replace existing fitments for matched SKUs.'
            : 'Mode: append fitments to existing mappings.';
    }

    if (!item.restrictToVendor) {
        return 'Vendor mapping: Store-wide (no vendor restriction)';
    }

    const vendorLabel = item.selectedVendorName || 'Unknown vendor';
    const normalizeLabel = `SKU normalize: ${item.normalizeSku ? 'true' : 'false'}`;

    if (!item.useVendorSkuSuffixRule) {
        return `Vendor mapping: ${vendorLabel} · SKU suffix rule disabled · ${normalizeLabel}`;
    }

    const suffixRule = item.vendorSkuSuffixRule?.trim();
    const suffixRuleLabel = suffixRule ? suffixRule : '(empty)';
    const updateLabel = item.updateSavedVendorSkuSuffixRule ? 'saved as default' : 'import-only';

    return `Vendor mapping: ${vendorLabel} · Suffix "${suffixRuleLabel}" (${updateLabel}) · ${normalizeLabel}`;
};

const formatCount = (value?: number) => (typeof value === 'number' ? value.toLocaleString() : '0');

const getRecordCountSummary = (item: FitmentImportHistoryItem) => {
    if (item.importFormat === 'csv_xlsx') {
        const parts = [
            `Parsed: ${formatCount(item.parsedRecordCount)}`,
        ];

        if (typeof item.csvXlsxImportedVehicleRecordCount === 'number') {
            parts.push(`Vehicle records: ${formatCount(item.csvXlsxImportedVehicleRecordCount)}`);
        }

        parts.push(`Product assignments: ${formatCount(item.csvXlsxAppliedAssignmentCount ?? item.appliedRecordCount)}`);

        return parts.join(', ');
    }

    return `Records Declared: ${formatCount(item.declaredRecordCount)}, Parsed: ${formatCount(item.parsedRecordCount)}, Applied: ${formatCount(item.appliedRecordCount)}`;
};

const shouldShowRecordCountSummary = (item: FitmentImportHistoryItem) => {
    return Boolean(
        item.parsedRecordCount
        || item.appliedRecordCount
        || item.declaredRecordCount
        || item.csvXlsxImportedVehicleRecordCount
        || item.csvXlsxAppliedAssignmentCount
    );
};

type StageProgress = {
    label: string;
    value: number;
};

const getStageProgress = (item: FitmentImportHistoryItem): StageProgress | null => {
    const stage = item.processingStage?.trim().toLowerCase();
    const declared = item.declaredRecordCount;
    const parsed = item.parsedRecordCount;
    const applied = item.appliedRecordCount;

    if (stage === 'parsing' && typeof declared === 'number' && declared > 0 && typeof parsed === 'number') {
        const value = Math.round((Math.min(parsed, declared) / declared) * 100);
        return {
            label: `Parsing rows ${formatCount(parsed)} / ${formatCount(declared)} (${value}%)`,
            value,
        };
    }

    if (stage === 'applying' && typeof parsed === 'number' && parsed > 0 && typeof applied === 'number') {
        const value = Math.round((Math.min(applied, parsed) / parsed) * 100);
        return {
            label: `Applying rows ${formatCount(applied)} / ${formatCount(parsed)} (${value}%)`,
            value,
        };
    }

    if (stage === 'pricing' && typeof parsed === 'number' && parsed > 0 && typeof applied === 'number') {
        const value = Math.round((Math.min(applied, parsed) / parsed) * 100);
        return {
            label: `Updating matched variants ${formatCount(applied)} / ${formatCount(parsed)} (${value}%)`,
            value,
        };
    }

    if (stage === 'exporting') {
        const value = typeof item.progressPercent === 'number' ? Math.max(0, Math.min(100, item.progressPercent)) : 0;
        return {
            label: 'Writing fitment export CSV',
            value,
        };
    }

    if (stage === 'tagging') {
        if (typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0) {
            const processed = Math.max(0, Math.min(item.taggingProcessedCount ?? 0, item.taggingTotalCount));
            const value = Math.round((processed / item.taggingTotalCount) * 100);
            const phase = item.taggingPhase?.trim().toLowerCase();
            const actionLabel = phase === 'fetching' ? 'Fetching product tags' : 'Applying product tags';
            return {
                label: `${actionLabel} ${formatCount(processed)} / ${formatCount(item.taggingTotalCount)} (${value}%)`,
                value,
            };
        }

        const value = typeof item.progressPercent === 'number' ? Math.max(0, Math.min(100, item.progressPercent)) : 0;
        return {
            label: 'Tagging changed products',
            value,
        };
    }

    return null;
};

const getStageDiagnostics = (item: FitmentImportHistoryItem) => {
    const stage = item.processingStage?.trim().toLowerCase();

    if (!stage) {
        return null;
    }

    if (stage === 'parsing') {
        return 'Current step: parsing input file and normalizing source rows.';
    }

    if (stage === 'applying') {
        return 'Current step: writing parsed rows to fitment records and deduplicating.';
    }

    if (stage === 'resolving_skus') {
        return 'Current step: looking up missing SKUs directly in Shopify.';
    }

    if (stage === 'waiting_shopify_sync') {
        return 'Current step: waiting for Shopify product and variant sync to finish.';
    }

    if (stage === 'processing_recovered_skus') {
        return 'Current step: applying fitment rows for SKUs recovered from Shopify.';
    }

    if (stage === 'pricing') {
        return 'Current step: matching ATD part numbers to Shopify variant SKUs and pushing price updates.';
    }

    if (stage === 'exporting') {
        return 'Current step: writing assigned product fitments to a downloadable CSV.';
    }

    if (stage === 'reading') {
        return 'Current step: reading synced Shopify product tags.';
    }

    if (stage === 'tagging') {
        return 'Current step: refreshing product tags for changed fitments.';
    }

    return null;
};

type Props = {
    items: FitmentImportHistoryItem[];
    archivingIds: Record<number, boolean>;
    onArchive: (queueId: number) => void;
    onDownload: (item: FitmentImportHistoryItem) => void;
    onDownloadReview?: (item: FitmentImportHistoryItem) => void;
    downloadingIds: Record<number, boolean>;
    downloadingReviewIds?: Record<number, boolean>;
    reviewingProductTagIds?: Record<number, boolean>;
    undoingProductTagIds?: Record<number, boolean>;
    onReviewProductTags?: (item: FitmentImportHistoryItem) => void;
    onUndoProductTags?: (item: FitmentImportHistoryItem) => void;
};

const ImportHistoryCards = ({ items, archivingIds, onArchive, onDownload, onDownloadReview, downloadingIds, downloadingReviewIds = {}, reviewingProductTagIds = {}, undoingProductTagIds = {}, onReviewProductTags, onUndoProductTags }: Props) => {
    return (
        <BlockStack gap="200">
            {items.map((item) => {
                const progressValue = typeof item.progressPercent === 'number'
                    ? Math.max(0, Math.min(100, item.progressPercent))
                    : undefined;
                const stageLabel = formatStageLabel(item.processingStage);
                const normalizedState = item.state.toLowerCase();
                const isProcessingState = normalizedState === 'processing'
                    || normalizedState.includes('tagging')
                    || normalizedState.includes('waiting for shopify sync')
                    || normalizedState.includes('resolving skus')
                    || normalizedState.includes('processing recovered skus')
                    || normalizedState.includes('exporting');
                const showProgress = isProcessingState && (progressValue === undefined || progressValue < 100);
                const stageProgress = getStageProgress(item);
                const stageDiagnostics = getStageDiagnostics(item);

                return (
                    <Card key={item.queueId}>
                        <BlockStack gap="150">
                            <InlineStack align="space-between" blockAlign="center" gap="300">
                                <Text as="h3" variant="headingSm">
                                    {item.originalFileName || 'Unnamed file'}
                                </Text>

                                <InlineStack gap="200" blockAlign="center">
                                    {getStatusBadge(item.state)}
                                    {item.importFormat === 'product_tags' && onReviewProductTags && (
                                        <Button
                                            variant="plain"
                                            onClick={() => onReviewProductTags(item)}
                                            loading={!!reviewingProductTagIds[item.queueId]}
                                        >
                                            {item.awaitingProductTagApproval ? 'Review' : 'Preview'}
                                        </Button>
                                    )}
                                    {item.importFormat === 'product_tags' && item.productTagReviewStatus?.toLowerCase() === 'applied' && onUndoProductTags && (
                                        <Button
                                            tone="critical"
                                            variant="plain"
                                            onClick={() => onUndoProductTags(item)}
                                            loading={!!undoingProductTagIds[item.queueId]}
                                        >
                                            Undo
                                        </Button>
                                    )}
                                    {item.storedFileName && (
                                        <Button
                                            variant="plain"
                                            onClick={() => onDownload(item)}
                                            loading={!!downloadingIds[item.queueId]}
                                        >
                                            Download
                                        </Button>
                                    )}
                                    {item.importFormat === 'csv_xlsx' && item.storedFileName && onDownloadReview && (
                                        <Button
                                            variant="plain"
                                            onClick={() => onDownloadReview(item)}
                                            loading={!!downloadingReviewIds[item.queueId]}
                                        >
                                            Review file
                                        </Button>
                                    )}
                                    <Button
                                        tone="critical"
                                        variant="plain"
                                        onClick={() => onArchive(item.queueId)}
                                        loading={!!archivingIds[item.queueId]}
                                    >
                                        Archive
                                    </Button>
                                </InlineStack>
                            </InlineStack>

                            <InlineStack gap="300" blockAlign="center" wrap>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Queued: {new Date(item.addedOn).toLocaleString()}
                                </Text>
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Format: {formatLabelMap[item.importFormat] ?? item.importFormat}
                                </Text>
                                {stageLabel && (
                                    <Text as="p" variant="bodySm" tone="subdued">
                                        Stage: {stageLabel}
                                    </Text>
                                )}
                            </InlineStack>

                            {showProgress && (
                                <BlockStack gap="100">
                                    <InlineStack align="space-between">
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            Overall progress
                                        </Text>
                                        {progressValue !== undefined && (
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                {progressValue}%
                                            </Text>
                                        )}
                                    </InlineStack>
                                    {progressValue !== undefined && <ProgressBar progress={progressValue} size="small" />}

                                    {stageProgress && (
                                        <BlockStack gap="050">
                                            <InlineStack align="space-between">
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {stageProgress.label}
                                                </Text>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {stageProgress.value}%
                                                </Text>
                                            </InlineStack>
                                            <ProgressBar progress={stageProgress.value} size="small" />
                                        </BlockStack>
                                    )}

                                    <Text as="p" variant="bodySm" tone="subdued">
                                        {item.processingMessage || 'Working on import...'}
                                    </Text>
                                </BlockStack>
                            )}

                            {stageDiagnostics && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                    {stageDiagnostics}
                                </Text>
                            )}

                            <Text as="p" variant="bodySm">
                                {getMappingRulesSummary(item)}
                            </Text>

                            {shouldShowRecordCountSummary(item) && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                    {getRecordCountSummary(item)}
                                </Text>
                            )}

                            {typeof item.taggingTotalCount === 'number' && item.taggingTotalCount > 0 && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Tagging: Processed {formatCount(item.taggingProcessedCount)} / {formatCount(item.taggingTotalCount)}, Updated {formatCount(item.taggingUpdatedCount)}
                                </Text>
                            )}

                            {item.importFormat === 'product_tags' && typeof item.productTagPreviewTotalCount === 'number' && (
                                <Text as="p" variant="bodySm" tone="subdued">
                                    Preview: {formatCount(item.productTagWillApplyCount)} row(s) ready, {formatCount(item.productTagWillCreateCount)} new vehicle value(s), {formatCount(item.productTagSkippedCount)} skipped across {formatCount(item.productTagPreviewProductCount)} product(s).
                                </Text>
                            )}

                            {item.error && (
                                <Text as="p" variant="bodySm" tone="critical">
                                    Error: {item.error}
                                </Text>
                            )}
                        </BlockStack>
                    </Card>
                );
            })}
        </BlockStack>
    );
};

export default ImportHistoryCards;
